import { useContext } from "react";
import { Box, GlobalStyles } from "@mui/material"; // 🌟 เพิ่ม GlobalStyles
import Grid from "@mui/material/Unstable_Grid2";
import { DashboardContext } from "../../contexts/DashboardContext";
import { useTranslation } from "react-i18next";
import OverviewCard from "../../components/dashboard/OverviewCard";
import OverviewPieCard from "../../components/dashboard/OverviewPieCard";
import { EmissionData } from "../../models/Emission";
import EmissionByCard from "../../components/dashboard/EmissionByCard";
import NumberFormatterUtils from "../../utils/numberFormatterUtils";
import { REPORT_GROUP } from "../../constants/report-group";
import { common, overview } from "../../i18n";
import { CategoryTypeData } from "../../models/emission-dashboard.types";
import EmissionByBusinessCardV2, {
  scopes,
} from "../../components/dashboard/EmissionByBusinessCard";
import EmissionCard, { byChart } from "../../components/dashboard/EmissionCard";
import { isScope3Open } from "../../config/config";

interface Props {
  preview?: boolean;
}

export interface ChartSeriesItem {
  id: number;
  value: number;
  label: string;
  color: string;
  percent?: number;
}

const Dashboard = ({ preview }: Props) => {
  const getSeriesData = (
    typesData: CategoryTypeData[] | undefined,
    scopeName: string,
  ): ChartSeriesItem[] => {
    if (!typesData || typesData.length === 0) return [];
    const matchedGroups = REPORT_GROUP.filter((g) => g.scope === scopeName);

    const series: ChartSeriesItem[] = typesData.map((type, index) => {
      let color = matchedGroups[index]?.colors?.[1] ?? "#cccccc";
      let title = i18n.language === "th" ? type.categoryTH : type.categoryEn;

      if (scopeName === "Scope-3") {
        const item = matchedGroups.find((g) => g.id === type.categoryEn);
        title = t(item?.title ?? type.categoryEn);
        color = item?.colors[1] ?? color;
      }

      return {
        id: index,
        value: type.value,
        label: title,
        color: color,
        percent: type.percent,
      };
    });

    const isAllZero = series.every((item) => item.value === 0);
    return isAllZero ? [] : series;
  };

  const context = useContext(DashboardContext);
  const dashboardData = context.dashboardData;

  const { t, i18n } = useTranslation([common, overview], {
    nsMode: "fallback",
  });

  const seriesScope1V2 = getSeriesData(
    context.dashboardData?.byTypes.s1.types,
    "Scope-1",
  );
  const seriesScope2V2 = getSeriesData(
    context.dashboardData?.byTypes.s2.types,
    "Scope-2",
  );
  const seriesScope3V2 = getSeriesData(
    context.dashboardData?.byTypes.s3.types,
    "Scope-3",
  );
  const seriesScope0V2 = getSeriesData(
    context.dashboardData?.byTypes.s0.types,
    "Outside-of-Scope",
  );

  const getGroup = (emission: EmissionData) => {
    return emission.scope;
  };

  const pieLgSize = isScope3Open ? 3 : 4;

  const byAssetRecordV2: Record<string, number> = {};
  const bySourceRecordV2: Record<string, number> = {};

  dashboardData?.byAssets.forEach((item) => {
    const asset = item.asset ?? "";
    byAssetRecordV2[asset] = item.value;
  });

  dashboardData?.bySources.forEach((item) => {
    const source = i18n.language === "th" ? item.categoryTH : item.categoryEn;
    bySourceRecordV2[source] = item.value;
  });

  return (
    <Box display={"flex"} flexDirection={"column"} gap={"20px"}>
      {/* 🌟 ย้าย CSS จากไฟล์ภายนอก มาฝังเป็น Global Styles ของหน้านี้แทน */}
      <GlobalStyles
        styles={{
          ".tooltip .MuiTableCell-body:nth-child(2), .tooltip .MuiTableCell-body:nth-child(3)":
            {
              color: "var(--gray4)",
            },
        }}
      />
      {/* Overview Card */}
      <Grid container display={"flex"} spacing={"20px"}>
        <Grid flexGrow={1}>
          <OverviewCard
            title={t("Emission")}
            info={t("Total-Emission-Summary-Description")}
            text1={`${NumberFormatterUtils.formatDynamic(
              dashboardData?.sumScopes.total ?? 0,
            )} tCO₂e`}
            text2={t("Total-Emissions-Scope-1-3")}
          />
        </Grid>
        <Grid flexGrow={1}>
          <OverviewCard
            title={t("Scope-1")}
            info={t("Scope-1.Summary-Description")}
            text1={`${NumberFormatterUtils.formatDynamic(
              dashboardData?.sumScopes.s1.percent ?? 0,
            )} %`}
            text2={`${NumberFormatterUtils.formatDynamic(
              dashboardData?.sumScopes.s1.value ?? 0,
            )} tCO₂e`}
            icon="/img/scope1.svg"
          />
        </Grid>
        <Grid flexGrow={1}>
          <OverviewCard
            title={t("Scope-2")}
            info={t("Scope-2.Summary-Description")}
            text1={`${NumberFormatterUtils.formatDynamic(
              dashboardData?.sumScopes.s2.percent ?? 0,
            )} %`}
            text2={`${NumberFormatterUtils.formatDynamic(
              dashboardData?.sumScopes.s2.value ?? 0,
            )} tCO₂e`}
            icon="/img/scope2.svg"
          />
        </Grid>
        {isScope3Open ? (
          <Grid flexGrow={1}>
            <OverviewCard
              title={t("Scope-3")}
              info={t("Scope-3.Summary-Description")}
              text1={`${NumberFormatterUtils.formatDynamic(
                dashboardData?.sumScopes.s3.percent ?? 0,
              )} %`}
              text2={`${NumberFormatterUtils.formatDynamic(
                dashboardData?.sumScopes.s3.value ?? 0,
              )} tCO₂e`}
              icon="/img/scope3.svg"
            />
          </Grid>
        ) : null}
        <Grid flexGrow={1}>
          <OverviewCard
            title={t("Outside-of-Scope")}
            info={t("Outside-of-Scope.Summary-Description")}
            text1={`${NumberFormatterUtils.formatDynamic(
              dashboardData?.sumScopes.s0.percent ?? 0,
            )} %`}
            text2={`${NumberFormatterUtils.formatDynamic(
              dashboardData?.sumScopes.s0.value ?? 0,
            )} tCO₂e`}
            icon="/img/other.svg"
          />
        </Grid>
      </Grid>

      {context.dashboardData && (
        <EmissionCard
          t={t}
          title={t("Emission")}
          info={t("Emission-Description")}
          defaultBy={byChart}
          showByDropdown
          groups={scopes}
          startDate={context.startDate}
          endDate={context.endDate}
          preview={preview}
          data={context.dashboardData}
        />
      )}
      {/* Pie Card */}
      <Grid container display={"flex"} spacing={"20px"} flexWrap={"wrap"}>
        <Grid
          xs={preview ? pieLgSize : 12}
          md={preview ? pieLgSize : 6}
          lg={pieLgSize}
        >
          <OverviewPieCard
            title={t("Scope-1")}
            info={t("Scope-1.Summary-Description")}
            icon="/img/scope1.svg"
            data={seriesScope1V2 ?? []}
            total={context.dashboardData?.byTypes.s1.total ?? 0}
          />
        </Grid>
        <Grid
          xs={preview ? pieLgSize : 12}
          md={preview ? pieLgSize : 6}
          lg={pieLgSize}
        >
          <OverviewPieCard
            title={t("Scope-2")}
            info={t("Scope-2.Summary-Description")}
            icon="/img/scope2.svg"
            data={seriesScope2V2 ?? []}
            total={context.dashboardData?.byTypes.s2.total ?? 0}
          />
        </Grid>

        {isScope3Open ? (
          <Grid
            xs={preview ? pieLgSize : 12}
            md={preview ? pieLgSize : 6}
            lg={pieLgSize}
          >
            <OverviewPieCard
              title={t("Scope-3")}
              info={t("Scope-3.Summary-Description")}
              icon="/img/scope3.svg"
              data={seriesScope3V2.filter((i) => i.value > 0) ?? []}
              total={context.dashboardData?.byTypes.s3.total ?? 0}
            />
          </Grid>
        ) : null}

        <Grid
          xs={preview ? pieLgSize : 12}
          md={preview ? pieLgSize : 6}
          lg={pieLgSize}
        >
          <OverviewPieCard
            title={t("Outside-of-Scope")}
            info={t("Outside-of-Scope.Summary-Description")}
            icon="/img/other.svg"
            data={seriesScope0V2 ?? []}
            total={context.dashboardData?.byTypes.s0.total ?? 0}
          />
        </Grid>
      </Grid>
      {/* Emission By Card */}
      <Grid container spacing={"20px"}>
        <Grid xs={preview ? 6 : 12} md={6}>
          <EmissionByCard
            title={t("Emissions-by-Assets")}
            description={t("Emissions-by-Assets-Desc")}
            data={byAssetRecordV2}
            top={5}
          />
        </Grid>
        <Grid xs={preview ? 6 : 12} md={6}>
          <EmissionByCard
            title={t("Emissions-by-Sources")}
            description={t("Emissions-by-Sources-Desc")}
            data={bySourceRecordV2}
            top={5}
          />
        </Grid>
      </Grid>
      <EmissionByBusinessCardV2 groups={scopes} getGroup={getGroup} />
    </Box>
  );
};

export default Dashboard;
